/**
 * PATH: packages/email-hub/src/feature/components/email-context-menu/OrganizationActions.tsx
 * FILE_ID: EMAILHUB-CONTEXTMENU-ORGANIZATION-ACTIONS-001
 * PURPOSE: Categorization, linking, delegation, scheduling, and star actions.
 * WHY: Groups organization-domain actions outside the provider frame.
 */
import {
  Briefcase,
  CalendarPlus,
  Link2,
  Lock,
  RotateCcw,
  Star,
  Tag,
  UserPlus,
} from 'lucide-react'
import { triggerComposeFromEmail } from '@quotech/ui/calendar'

import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
} from '../../../_internal/components/ContextMenu'
import { CATEGORY_PICKER_OPTIONS } from '../../constants/categories'
import { buildComposeFromEmailPayload } from '../../lib/composeFromEmail'
import type { EmailContextMenuController } from './useEmailContextMenuController'

export function OrganizationActions({
  controller,
}: {
  controller: EmailContextMenuController
}) {
  const {
    applyCategory,
    ctxMenu,
    inviteEmailWatcher,
    inviteMembers,
    navigate,
    onPatchLocal,
    onRefetch,
    orgCustomCategories,
    personalCategories,
    resetCategory,
    setCtxMenu,
    setLinkModal,
    toast,
    toggleStar,
  } = controller
  if (!ctxMenu) return null

  const email = ctxMenu.email
  const close = () => setCtxMenu(null)
  const watcherIds = new Set((email.watchers ?? []).map((watcher) => watcher.user_id))
  const assigneeId = email.assignment?.assignee_user_id ?? null
  const inviteCandidates = inviteMembers?.filter(
    (member) => !watcherIds.has(member.id) && member.id !== assigneeId,
  )

  return (
    <>
      <ContextMenuSub label="Set category" icon={Tag}>
        {CATEGORY_PICKER_OPTIONS.map((option) => (
          <ContextMenuItem
            key={option.key}
            dot={option.dotColor}
            active={email.category === option.key}
            onClick={() => {
              close()
              void applyCategory(email.id, option.key, option.label)
            }}
          >
            {option.label}
            {email.category === option.key && email.category_locked && (
              <Lock className="ml-2 h-3 w-3 text-muted-foreground" />
            )}
          </ContextMenuItem>
        ))}
        {orgCustomCategories.length > 0 && (
          <>
            <ContextMenuSeparator />
            {orgCustomCategories.map((option) => (
              <ContextMenuItem
                key={option.slug}
                dot={option.color || '#0891b2'}
                active={email.category === option.slug}
                onClick={() => {
                  close()
                  void applyCategory(email.id, option.slug, option.label)
                }}
              >
                {option.label}
                {email.category === option.slug && email.category_locked && (
                  <Lock className="ml-2 h-3 w-3 text-muted-foreground" />
                )}
              </ContextMenuItem>
            ))}
          </>
        )}
        {personalCategories.length > 0 && (
          <>
            <ContextMenuSeparator />
            {personalCategories.map((option) => (
              <ContextMenuItem
                key={option.slug}
                dot={option.color || '#6366f1'}
                active={email.category === option.slug}
                onClick={() => {
                  close()
                  void applyCategory(email.id, option.slug, option.label)
                }}
              >
                {option.label}
                {email.category === option.slug && email.category_locked && (
                  <Lock className="ml-2 h-3 w-3 text-muted-foreground" />
                )}
              </ContextMenuItem>
            ))}
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem
          icon={RotateCcw}
          onClick={() => {
            close()
            void resetCategory(email.id)
          }}
        >
          Reset to AI
        </ContextMenuItem>
      </ContextMenuSub>
      <ContextMenuItem
        icon={Link2}
        onClick={() => {
          setLinkModal({
            emailId: email.id,
            emailSubject: email.subject || '(no subject)',
            emailBody: email.body_text || email.body_preview || '',
            emailFrom: email.from_address || '',
          })
          close()
        }}
      >
        Link to Job
      </ContextMenuItem>
      {inviteMembers && inviteMembers.length > 0 && inviteCandidates?.length === 0 && (
        <ContextMenuItem icon={UserPlus} disabled>
          Everyone already watching
        </ContextMenuItem>
      )}
      {inviteCandidates && inviteCandidates.length > 0 && (
        <ContextMenuSub label="Send to Team Member" icon={UserPlus}>
          {inviteCandidates.map((member) => (
            <ContextMenuItem
              key={member.id}
              onClick={async () => {
                close()
                try {
                  await inviteEmailWatcher(email.id, member.id)
                  toast(`Sent to ${member.name}`, 'success')
                  onRefetch?.()
                } catch (err) {
                  toast(
                    `Could not send: ${(err as Error).message || 'unknown error'}`,
                    'error',
                  )
                }
              }}
            >
              {member.name}
              {member.email && (
                <span className="ml-2 text-[10px] text-muted-foreground truncate">
                  {member.email}
                </span>
              )}
            </ContextMenuItem>
          ))}
        </ContextMenuSub>
      )}
      <ContextMenuItem
        icon={Briefcase}
        onClick={() => {
          const prefill = [
            email.subject ? `Subject: ${email.subject}` : '',
            email.from_name || email.from_address
              ? `From: ${email.from_name || ''} <${email.from_address}>`
              : '',
            '',
            email.body_text || email.body_preview || '',
          ].filter(Boolean).join('\n')
          const params = new URLSearchParams({ create: '1', prefill_text: prefill })
          close()
          navigate(`/jobs?${params.toString()}`)
        }}
      >
        Create Job from Email
      </ContextMenuItem>
      <ContextMenuItem
        icon={CalendarPlus}
        onClick={() => {
          const payload = buildComposeFromEmailPayload(email)
          close()
          triggerComposeFromEmail(payload, navigate)
        }}
      >
        Schedule Meeting from Email
      </ContextMenuItem>
      <ContextMenuItem
        icon={Star}
        onClick={async () => {
          const { id, is_starred } = email
          onPatchLocal?.(id, { is_starred: !is_starred })
          try {
            await toggleStar(id, is_starred)
            onRefetch?.()
          } catch {
            onRefetch?.()
          }
          close()
        }}
      >
        {email.is_starred ? 'Unstar' : 'Star'}
      </ContextMenuItem>
      <ContextMenuSeparator />
    </>
  )
}
